import { retrieveRawInitData } from '@telegram-apps/sdk-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app';
import { bootstrap, loadWebUser } from './app/bootstrap';
import { preloadOnboarding } from './app/router/lazyPages';
import { CONNECT_PATH, handleLostSession, isGuestCreatedNow, isLandingVisitor } from './shared/lib/webSession';
import { MAINTENANCE_EVENT, isMaintenanceError, setRequestFailureListener } from './shared/api';
import { env } from './shared/config';
import { flushQueue, reportError, reportGlobalErrors, reportRequestFailure } from './shared/lib/reportError';
import { backupLaunchParams, restoreLaunchParams } from './shared/lib/telegram/launchParamsBackup';
import { syncSkeletonShimmer } from './shared/lib/syncSkeletonShimmer';
import { isDevTelegramMock, isTelegram, setTelegramPlatform } from './shared/lib/platform';
import './app/styles/index.css';

const container = document.getElementById('root');
if (!container) throw new Error('Не найден #root — проверь index.html');

const root = createRoot(container);
syncSkeletonShimmer();

/** Экран технических работ уже показан — приложение поверх него не рисуем. */
let maintenanceShown = false;

function render() {
  // `/me` с 503 может прийти раньше, чем bootstrap() дождётся переводов, — без
  // этой проверки приложение перерисовалось бы поверх экрана работ.
  if (maintenanceShown) return;
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

/**
 * Технические работы (`MAINTENANCE_MODE` на бэкенде) — экран вместо всего
 * приложения, на каком бы маршруте ни открыли. Срабатывает и на старте (`/me`
 * ответил 503), и посреди работы: любой запрос с 503 `maintenance` шлёт
 * `MAINTENANCE_EVENT` (`shared/api/http.ts`).
 */
function showMaintenance() {
  if (maintenanceShown) return;
  maintenanceShown = true;
  void import('./app/maintenance').then(({ MaintenanceScreen }) => {
    root.render(
      <StrictMode>
        <MaintenanceScreen />
      </StrictMode>,
    );
  });
}

/**
 * «Попробовать бесплатно»: гость уже заведён — приложение рисуется сразу,
 * без перезагрузки страницы. Перезагрузка заново качала бандл, и между
 * экранами мелькали пустой фон и спиннер. С корня — `RootRedirect` сам
 * отправит гостя в онбординг.
 */
function startAfterWelcome() {
  window.history.replaceState(null, '', '/');
  const currentUser = loadWebUser();
  render();
  currentUser.catch((error: unknown) => {
    if (isMaintenanceError(error)) showMaintenance();
  });
}

/**
 * Веб-версия без входа и не из рекламы — экран входа вместо приложения (см.
 * `bootstrapWeb`). Отдельным чанком: в мини-аппе он не нужен.
 */
function showWelcome(resume = false) {
  // «Заниматься без VPN» из мини-аппа — свой экран: только вход, без «Попробовать».
  if (window.location.pathname === CONNECT_PATH) {
    void import('./features/web-auth').then(({ ConnectScreen }) => {
      root.render(
        <StrictMode>
          <ConnectScreen />
        </StrictMode>,
      );
    });
    return;
  }
  void import('./features/web-auth').then(({ WelcomeScreen }) => {
    // Скорее всего, дальше «Попробовать бесплатно» — онбординг качаем, пока
    // человек читает экран, а не после нажатия (иначе — спиннер).
    window.setTimeout(() => void preloadOnboarding(), 1500);
    root.render(
      <StrictMode>
        <WelcomeScreen onStarted={resume ? render : startAfterWelcome} resume={resume} />
      </StrictMode>,
    );
  });
}

/**
 * Открыли не из Telegram — обычной ссылкой в браузере: работаем как
 * веб-версия, с входом по сессии вместо initData (см. WEB.md). SDK ищет
 * данные запуска в адресе и в sessionStorage (там они переживают
 * перезагрузку внутри Telegram). В dev без данных запуска — тоже веб-версия;
 * мини-апп с подменой окружения — по флагу `?tg=1` (см. `isDevTelegramMock`).
 */
function hasInitData(): boolean {
  try {
    return Boolean(retrieveRawInitData());
  } catch {
    return false;
  }
}

/**
 * Свёрнутый надолго мини-апп система может выгрузить, и Telegram поднимает
 * его заново уже без данных запуска — тогда берём копию с прошлого открытия
 * (см. `launchParamsBackup.ts`).
 */
function openedOutsideTelegram(): boolean {
  // В dev — по адресу, а не `hasInitData()`: после запуска с `?tg=1` подменённые
  // данные остаются в sessionStorage, и вкладка уже не вернулась бы в веб.
  if (env.isDev) return !isDevTelegramMock();
  if (hasInitData()) {
    backupLaunchParams();
    return false;
  }
  return !(restoreLaunchParams() && hasInitData());
}

setTelegramPlatform(!openedOutsideTelegram());
// `index.html` угадывает веб-версию до загрузки скрипта (класс `web-wide`);
// здесь решение точное — в Telegram колонку возвращаем до первой отрисовки.
if (isTelegram()) document.documentElement.classList.remove('web-wide');

// Даже если инициализация SDK не удалась (старый клиент),
// приложение должно отрисоваться: ErrorBoundary и экраны ошибок объяснят,
// что пошло не так, — это лучше вечного лоадера Telegram.
reportGlobalErrors();
// Любой неудачный запрос — в служебный чат: экран ошибки видит человек, а не мы.
// Кроме потерянной веб-сессии: её разбирает `handleLostSession` (в Telegram
// сессий нет — там 401 про initData, и он нужен в отчёте).
setRequestFailureListener((error) => {
  if (!isTelegram() && handleLostSession(error)) return;
  reportRequestFailure(error);
});
window.addEventListener(MAINTENANCE_EVENT, showMaintenance);
/**
 * Гость веб-версии вернулся на корень с незаконченным онбордингом — сначала
 * экран приветствия с «Продолжить» и «Войти» (`WelcomeScreen`, `resume`), а
 * не сразу онбординг. Не для нового гостя (заведён в этой загрузке — реклама
 * ведёт прямо в онбординг), не со статьи (у того онбординга нет вовсе) и не
 * по внутренним адресам: перезагрузка посреди онбординга остаётся в нём.
 */
async function isReturningGuestMidOnboarding(currentUser: ReturnType<typeof loadWebUser>): Promise<boolean> {
  if (isTelegram() || window.location.pathname !== '/' || window.location.search.includes('talk=')) return false;
  if (isGuestCreatedNow() || isLandingVisitor()) return false;
  try {
    const user = await currentUser;
    return user.isGuest && !user.onboardingCompleted;
  } catch {
    return false;
  }
}

bootstrap().then(async ({ currentUser }) => {
  if (!currentUser) {
    showWelcome();
    return;
  }
  if (await isReturningGuestMidOnboarding(currentUser)) {
    showWelcome(true);
    return;
  }
  render();
  // Отчёты, не ушедшие в прошлый раз (не было сети), — теперь уже со входом.
  if (!env.isDev) flushQueue();
  currentUser.catch((error: unknown) => {
    if (isMaintenanceError(error)) showMaintenance();
  });
}, (error: unknown) => {
  console.error('[app] bootstrap failed', error);
  reportError('error', error);
  render();
});
